import type { MultiSelectTextFieldProps } from "@/ui/composed/pickers/MultiSelectTextField";

/**
 * Form field types enum
 */
enum FormFieldType {
  Address = "address",
  CheckBox = "checkbox",
  Date = "date",
  Select = "select",
  Text = "text",
}

/**
 * Shared props for all form field variants
 */
interface SharedFormFieldProps extends ViewWithTestID {
  hasError?: boolean;
  isDisabled?: boolean;
  isHidden?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isSearchable?: MultiSelectTextFieldProps["isSearchable"];
  leftText?: string;
  name: string;
  options?: MultiSelectTextFieldProps["options"];
  placeholder?: string;
  rightText?: string;
  subLabel?: string;
}

/**
 * Form field variant with type-value-onChange correlation
 */
interface FormFieldVariant<T, Type extends FormFieldType> {
  onChange: (value?: T) => void;
  type: Type;
  value?: T;
}

/**
 * Discriminated union of all form field prop types
 * Ensures type, value, and onChange are correlated for each field type
 */
type FormFieldProps = (
  | FormFieldVariant<boolean, FormFieldType.CheckBox>
  | FormFieldVariant<Date, FormFieldType.Date>
  | FormFieldVariant<GooglePlacesAddress, FormFieldType.Address>
  | (FormFieldVariant<string, FormFieldType.Select> &
      Required<Pick<MultiSelectTextFieldProps, "options">>)
  | FormFieldVariant<string, FormFieldType.Text>
) &
  SharedFormFieldProps;

/**
 * Configuration for a single form field's validation rules
 */
interface FormFieldConfig<T extends FormFieldType = FormFieldType> {
  isHidden?: boolean;
  isRequired?: boolean;
  type: T;
}

/**
 * Type-safe record of field configurations indexed by field names
 * Enforces that all enum values must be present in the configuration
 *
 * @example
 * enum MyFieldNames {
 *   Email = "email",
 *   Phone = "phone",
 * }
 *
 * const config: ValidationSchema<MyFieldNames> = {
 *   [MyFieldNames.Email]: { isRequired: true, type: FormFieldType.Text },
 *   [MyFieldNames.Phone]: { isRequired: false, type: FormFieldType.Text },
 *   // TypeScript error if any enum value is missing
 * };
 */
type ValidationSchema<T extends string> = Record<T, FormFieldConfig>;

/**
 * Result of form validation
 */
interface FormValidationResult {
  errors: Record<string, boolean>;
  isValid: boolean;
}

/**
 * Picks keys from a union type while preserving the discriminated union structure.
 * When T is a union (A | B | C), this becomes: Pick<A, K> | Pick<B, K> | Pick<C, K>
 */
type PickFromUnion<T, K extends keyof T> = T extends unknown
  ? Pick<T, K>
  : never;

/**
 * Form field with only type and value properties.
 * Preserves the discriminated union structure so each type/value pair remains correlated.
 */
type FormFieldTypeValue = PickFromUnion<FormFieldProps, "type" | "value">;

export { FormFieldType };
export type {
  FormFieldConfig,
  FormFieldProps,
  FormFieldTypeValue,
  FormValidationResult,
  ValidationSchema,
};
